import React from 'react';
import cn from 'classnames';
import { ChevronLeftIcon, Logo, LogoutIcon } from '@components/Icons';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import styles from './Onboarding.module.scss';
import { useAuthContext } from '@hooks/use-auth-context';
import FooterLinks from '@components/FooterLinks';
import gradientBG from '@app/img/onboarding/gradient_bg.png';
import ImportingContactsAlert from '@components/Alert/ImportingContactsAlert';
import { useSelector } from 'react-redux';
import { selectUserHasFlag } from '@reducers/user/metadata';

const LogoutButton = () => {
  const { cleanupAndLogout } = useAuthContext();

  const confirmLogout = () => {
    window.app.confirm(
      {
        title: '',
        message: 'Are you sure you want to sign out?',
        action: 'Sign out',
        variant: 'danger',
        forceDarkMode: true,
      },
      (confirmed: boolean) => {
        if (confirmed) {
          cleanupAndLogout();
        }
      },
    );
  };

  return (
    <button className={styles.footerBtn} onClick={confirmLogout}>
      <LogoutIcon width={14} color="textSecondary" /> Sign Out
    </button>
  );
};

const OnboardingFooter = ({
  onBackBtnClick,
  hideBackBtn,
  showImportingContactsAlert,
}: {
  onBackBtnClick: () => void;
  hideBackBtn?: boolean;
  showImportingContactsAlert?: boolean;
}) => {
  const forceShowImportingContactsAlert = useSelector(
    selectUserHasFlag('forceShowImportingContactsAlert'),
  );

  return (
    <div className={styles.footer}>
      <LogoutButton />
      <div className={styles.links}>
        <FooterLinks />
      </div>

      {!hideBackBtn && (
        <button
          className={cn(styles.footerBtn, styles.backBtn, !onBackBtnClick && styles.hidden)}
          onClick={onBackBtnClick}
        >
          <ChevronLeftIcon width="5.5" /> Back
        </button>
      )}

      {(forceShowImportingContactsAlert || showImportingContactsAlert) && (
        <ImportingContactsAlert className={styles.importingContactsAlert} />
      )}
    </div>
  );
};

export default function OnboardingLayout({
  children,
  includeLogo = true,
  includeFooter = true,
  responsive = false,
  onBackBtnClick,
  hideBackBtn,
  showImportingContactsAlert,
}: {
  children: React.ReactNode;
  includeLogo?: boolean;
  includeFooter?: boolean;
  responsive?: boolean;
  onBackBtnClick?: () => void;
  hideBackBtn?: boolean;
  showImportingContactsAlert?: boolean;
}) {
  return (
    <div
      className={cn(
        styles.container,
        responsive && styles.responsive,
        !includeLogo && styles.noLogo,
      )}
    >
      <div className={styles.bgImageContainer}>
        <img src={gradientBG} className={styles.bgImage} />
      </div>
      <div className={styles.scrollingContainer}>
        <StateBasedFadeTransition
          state={String(includeLogo)}
          elementByStateMap={{
            true: <Logo className={styles.logo} width={37} />,
            false: <span />,
          }}
        />
        {children}
        <StateBasedFadeTransition
          state={String(includeFooter)}
          elementByStateMap={{
            true: (
              <OnboardingFooter
                onBackBtnClick={onBackBtnClick}
                hideBackBtn={hideBackBtn}
                showImportingContactsAlert={showImportingContactsAlert}
              />
            ),
            false: <span />,
          }}
        />
      </div>
    </div>
  );
}
